/**
 * Settings, as one navigable page instead of two rows of tabs.
 *
 * A left rail lists sections grouped into Preferences (every user) and
 * Administration (role-gated), with a search box that filters sections by
 * name and keywords. Every control on this page is wired to something that
 * reads it — the five former "analysis tuning" tabs of sliders nothing
 * consumed are gone on purpose, not hidden behind an Advanced flag. If a
 * setting comes back, its consumer comes back with it.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  MenuItem,
  Paper,
  Select,
  Slider,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  AdminPanelSettings as AdminPanelIcon,
  DarkMode as DarkModeIcon,
  Brightness4 as SoftDarkIcon,
  Brightness5 as SoftLightIcon,
  Check as CheckIcon,
  Hub as HubIcon,
  LightMode as LightModeIcon,
  Map as MapIcon,
  Palette as PaletteIcon,
  PhoneAndroid as PhoneAndroidIcon,
  Route as RouteIcon,
  Schedule as ScheduleIcon,
  Search as SearchIcon,
  SettingsBrightness as SystemModeIcon,
  Settings as SettingsIcon,
  Speed as PerformanceIcon,
  Videocam as CameraLocationsIcon,
  Layers as ReferenceLayersIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useAuthStore, useSettingsStore } from '../stores';
import DeviceReferenceManager from '../components/admin/DeviceReferenceManager';
import ItineraryAdminPanel from '../components/admin/ItineraryAdminPanel';
import FeatureFlagsAdminPanel from '../components/admin/FeatureFlagsAdminPanel';
import AccessControlAdminPanel from '../components/admin/AccessControlAdminPanel';
import CameraOverlaysAdminPanel from '../components/admin/CameraOverlaysAdminPanel';
import ReferenceLayersAdminPanel from '../components/admin/ReferenceLayersAdminPanel';
import PathfinderTrackFiltersAdmin from '../components/admin/PathfinderTrackFiltersAdmin';
import MonitorModulesAdminPanel from '../components/admin/MonitorModulesAdminPanel';
import { colorSchemes, type ColorScheme } from '../theme/colorSchemes';
import { getBackgroundPreset, type ThemeIntensity } from '../theme/tokens';
import ColorPickerWithPalette from '../components/common/ColorPickerWithPalette';
import InfoHint from '../components/common/InfoHint';
import {
  getBrowserTimeZone,
  getTimeZoneOptions,
  LOCAL_TIMEZONE_PREFERENCE,
} from '../utils/time';

let MQTTConnectionSettings: React.ComponentType<any> | null = null;
let SystemMetrics: React.ComponentType<any> | null = null;
try {
  const rfModule = require('@signals/pathfinder-rf-ui');
  MQTTConnectionSettings = rfModule.MQTTConnectionSettings;
  SystemMetrics = rfModule.SystemMetrics;
} catch { /* RF module not available */ }

interface SettingsSection {
  id: string;
  label: string;
  group: 'Preferences' | 'Administration';
  icon: React.ReactElement;
  /** Extra words the rail search should match beyond the label. */
  keywords: string;
  hint: string;
  render: () => React.ReactNode;
}

const SECTION_STORAGE = 'settings-section';

const THEME_MODES: Array<{
  value: 'light' | 'soft-light' | 'soft-dark' | 'dark' | 'system';
  label: string;
  icon: React.ReactElement;
}> = [
  { value: 'light', label: 'Light', icon: <LightModeIcon sx={{ fontSize: 18 }} /> },
  { value: 'soft-light', label: 'Soft light', icon: <SoftLightIcon sx={{ fontSize: 18 }} /> },
  { value: 'soft-dark', label: 'Soft dark', icon: <SoftDarkIcon sx={{ fontSize: 18 }} /> },
  { value: 'dark', label: 'Dark', icon: <DarkModeIcon sx={{ fontSize: 18 }} /> },
  { value: 'system', label: 'System', icon: <SystemModeIcon sx={{ fontSize: 18 }} /> },
];

const PRESET_SCHEMES = colorSchemes.filter((s) => s.id !== 'custom');

function resolveIntensity(
  themeMode: 'light' | 'soft-light' | 'soft-dark' | 'dark' | 'system',
): ThemeIntensity {
  if (themeMode !== 'system') return themeMode;
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function schemeStripes(
  scheme: ColorScheme,
  themeMode: 'light' | 'soft-light' | 'soft-dark' | 'dark' | 'system',
  customPrimary: string,
  customSecondary: string,
): [string, string, string, string] {
  const surfaces = getBackgroundPreset(resolveIntensity(themeMode));
  const primary = scheme.id === 'custom' ? customPrimary : scheme.colors.primary;
  const secondary = scheme.id === 'custom' ? customSecondary : scheme.colors.secondary;
  return [surfaces.default, surfaces.paper, scheme.colors.accent || primary, secondary];
}

function themeModeLabel(mode: string): string {
  return THEME_MODES.find((m) => m.value === mode)?.label || mode;
}

// ---------------------------------------------------------------- appearance

const AppearanceSection: React.FC = () => {
  const { settings, updateAppearance } = useSettingsStore();
  const { themeMode, colorScheme, customPrimaryColor, customSecondaryColor, hideWidgetHeaders } =
    settings.appearance;
  const selectedScheme = colorSchemes.find((s) => s.id === colorScheme) || colorSchemes[1];
  const intensity = resolveIntensity(themeMode);
  const surfaces = getBackgroundPreset(intensity);
  const accent = colorScheme === 'custom'
    ? customPrimaryColor
    : (selectedScheme.colors.accent || selectedScheme.colors.primary);
  const brand = colorScheme === 'custom' ? customSecondaryColor : selectedScheme.colors.secondary;
  const isCustom = colorScheme === 'custom';
  const previewLabel = `Preview · ${themeModeLabel(themeMode)}, ${isCustom ? 'Custom' : selectedScheme.name}`;

  return (
    <>
      <Box sx={{ mb: 2.5 }}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Appearance</Typography>
          <InfoHint title="Appearance">
            Theme intensity and color scheme for the whole application.
          </InfoHint>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Theme, color scheme, and dashboard chrome
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 650, fontSize: '1.05rem' }}>
                  Theme mode
                </Typography>
                <InfoHint title="Theme mode">
                  From bright light to deep dark, or follow the operating system.
                </InfoHint>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
                Follows the OS when set to System.
              </Typography>
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={themeMode}
                onChange={(_, value) => {
                  if (value) updateAppearance({ themeMode: value });
                }}
                sx={{
                  flexWrap: 'wrap',
                  '& .MuiToggleButton-root': {
                    textTransform: 'none',
                    color: 'text.secondary',
                    borderColor: 'divider',
                    px: 0.5,
                    py: 0.75,
                    minWidth: 0,
                    flexDirection: 'column',
                    gap: 0.25,
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    lineHeight: 1.15,
                    '&.Mui-selected': {
                      color: 'primary.main',
                      bgcolor: 'transparent',
                      borderColor: 'primary.main',
                      '&:hover': { bgcolor: 'action.hover' },
                    },
                  },
                }}
              >
                {THEME_MODES.map((mode) => (
                  <ToggleButton key={mode.value} value={mode.value} aria-label={mode.label}>
                    {mode.icon}
                    {mode.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>

              <Box sx={{ mt: 2.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 650, display: 'block', mb: 0.75 }}>
                  {previewLabel}
                </Typography>
                <Box
                  sx={{
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    border: 1,
                    borderColor: 'divider',
                    bgcolor: surfaces.default,
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: '#8B1E1E',
                      color: '#fff',
                      textAlign: 'center',
                      fontSize: '0.58rem',
                      fontWeight: 800,
                      letterSpacing: 0.8,
                      py: 0.35,
                    }}
                  >
                    UNCLASSIFIED // COMMERCIAL
                  </Box>
                  <Box sx={{ display: 'flex', minHeight: 132 }}>
                    <Box
                      sx={{
                        flex: 1.35,
                        position: 'relative',
                        bgcolor: surfaces.paper,
                        backgroundImage:
                          'linear-gradient(rgba(128,128,128,0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(128,128,128,0.12) 1px, transparent 1px)',
                        backgroundSize: '18px 18px',
                      }}
                    >
                      <Box
                        component="svg"
                        viewBox="0 0 120 80"
                        sx={{ position: 'absolute', inset: 10, width: 'calc(100% - 20px)', height: 'calc(100% - 20px)' }}
                      >
                        <polyline
                          points="8,62 32,48 54,52 78,28 108,18"
                          fill="none"
                          stroke={brand}
                          strokeWidth="2.4"
                          strokeLinejoin="round"
                        />
                        <circle cx="108" cy="18" r="3.2" fill={accent} />
                      </Box>
                    </Box>
                    <Box sx={{ flex: 1, p: 1, display: 'flex', flexDirection: 'column', gap: 0.65 }}>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {[
                          ['Devices', '142'],
                          ['Events', '7'],
                          ['Dwells', '23'],
                        ].map(([label, value]) => (
                          <Box
                            key={label}
                            sx={{
                              flex: 1,
                              bgcolor: surfaces.paper,
                              border: 1,
                              borderColor: 'divider',
                              borderRadius: 0.75,
                              px: 0.6,
                              py: 0.45,
                            }}
                          >
                            <Typography sx={{ fontSize: '0.58rem', color: 'text.secondary', lineHeight: 1 }}>
                              {label}
                            </Typography>
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: brand, lineHeight: 1.25 }}>
                              {value}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                      <Box
                        sx={{
                          bgcolor: surfaces.paper,
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 0.75,
                          px: 0.75,
                          py: 0.6,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                        }}
                      >
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: brand, flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, lineHeight: 1.2 }} noWrap>
                            Emitter lost
                          </Typography>
                          <Typography sx={{ fontSize: '0.58rem', color: 'text.secondary' }} noWrap>
                            2 min · Route 7
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 650, fontSize: '1.05rem', mb: 0.5 }}>
                Dashboards
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ py: 0.5, minHeight: 44 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography sx={{ fontWeight: 650, fontSize: '0.9rem' }}>
                      Hide widget headers
                    </Typography>
                    <InfoHint title="Widget headers">
                      Render dashboard widgets without their title bars for a denser wall display.
                    </InfoHint>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Gives dashboards more room for content.
                  </Typography>
                </Box>
                <Switch
                  color="primary"
                  checked={hideWidgetHeaders}
                  onChange={(e) => updateAppearance({ hideWidgetHeaders: e.target.checked })}
                />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
                sx={{ mb: 0.5 }}
              >
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="h6" sx={{ fontWeight: 650, fontSize: '1.05rem' }}>
                    Color scheme
                  </Typography>
                  <InfoHint title="Color scheme">
                    Background and surface come from theme mode. Accent and secondary come from
                    the scheme — buttons, selections, and alerts.
                  </InfoHint>
                </Stack>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => updateAppearance({ colorScheme: 'custom' })}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    color: isCustom ? 'primary.main' : 'text.secondary',
                  }}
                >
                  Custom
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
                Background, surface, accent, and secondary.
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr' },
                  gap: 1,
                }}
              >
                {PRESET_SCHEMES.map((scheme) => {
                  const isSelected = colorScheme === scheme.id;
                  const stripes = schemeStripes(
                    scheme,
                    themeMode,
                    customPrimaryColor,
                    customSecondaryColor,
                  );
                  return (
                    <Box
                      key={scheme.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => updateAppearance({ colorScheme: scheme.id })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          updateAppearance({ colorScheme: scheme.id });
                        }
                      }}
                      sx={{
                        height: 60,
                        borderRadius: 1.5,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        border: 1.5,
                        borderColor: isSelected ? stripes[3] : 'divider',
                        bgcolor: 'transparent',
                        position: 'relative',
                        outline: 'none',
                        '&:hover': { borderColor: stripes[3] },
                      }}
                    >
                      <Box sx={{ display: 'flex', height: 28 }}>
                        {stripes.map((color, i) => (
                          <Box key={`${scheme.id}-${i}`} sx={{ flex: 1, bgcolor: color }} />
                        ))}
                      </Box>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.4}
                        sx={{ px: 0.75, height: 32 }}
                      >
                        <Typography
                          noWrap
                          sx={{
                            flex: 1,
                            fontSize: '0.72rem',
                            fontWeight: isSelected ? 700 : 550,
                            lineHeight: 1.15,
                          }}
                        >
                          {scheme.name}
                        </Typography>
                        {scheme.id === 'endurion' && (
                          <Chip
                            label="default"
                            size="small"
                            sx={{ height: 16, fontSize: '0.55rem', fontWeight: 700, '& .MuiChip-label': { px: 0.5 } }}
                          />
                        )}
                        {isSelected && <CheckIcon sx={{ fontSize: 14, color: stripes[3] }} />}
                      </Stack>
                    </Box>
                  );
                })}
              </Box>

              {isCustom && (
                <Box
                  sx={{
                    mt: 1.5,
                    p: 2,
                    border: 1.5,
                    borderColor: brand,
                    borderRadius: 1.5,
                    bgcolor: 'background.default',
                  }}
                >
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="flex-start">
                    <Box sx={{ flex: '1 1 260px', width: '100%', minWidth: 0 }}>
                      <ColorPickerWithPalette
                        label="Primary color"
                        value={customPrimaryColor || '#2E86AB'}
                        onChange={(v) => updateAppearance({ customPrimaryColor: v })}
                        helperText="Used for accent surfaces and selections."
                      />
                    </Box>
                    <Box sx={{ flex: '1 1 260px', width: '100%', minWidth: 0 }}>
                      <ColorPickerWithPalette
                        label="Secondary color"
                        value={customSecondaryColor || '#A23B72'}
                        onChange={(v) => updateAppearance({ customSecondaryColor: v })}
                        helperText="Used for alerts and sparse highlights."
                      />
                    </Box>
                  </Stack>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

// -------------------------------------------------------------------- graphs

const GRAPH_SLIDERS: Array<{
  key: 'nodeSize' | 'linkDistance' | 'chargeStrength' | 'linkCurvature' | 'linkOpacity';
  label: string;
  hint: string;
  min: number;
  max: number;
  step: number;
}> = [
  { key: 'nodeSize', label: 'Node size', hint: 'Radius of entity nodes.', min: 4, max: 20, step: 1 },
  { key: 'linkDistance', label: 'Link distance', hint: 'Resting length between connected nodes.', min: 40, max: 300, step: 10 },
  { key: 'chargeStrength', label: 'Charge strength', hint: 'Negative pushes nodes apart; closer to zero packs them tighter.', min: -1000, max: -50, step: 25 },
  { key: 'linkCurvature', label: 'Link curvature', hint: '0 draws straight lines, 1 full arcs.', min: 0, max: 1, step: 0.05 },
  { key: 'linkOpacity', label: 'Link opacity', hint: 'How prominent connections are against the background.', min: 0.05, max: 1, step: 0.05 },
];

const GraphsSection: React.FC = () => {
  const { settings, updateLinkAnalysis, resetDefaults } = useSettingsStore();
  const values = settings.visualization.linkAnalysis;

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Link Analysis graphs</Typography>
        <InfoHint title="Link Analysis graphs">
          Default force-layout for the Link Analysis network view. New graphs open with these
          values.
        </InfoHint>
      </Stack>

      <Grid container spacing={3}>
        {GRAPH_SLIDERS.map(({ key, label, hint, min, max, step }) => (
          <Grid item xs={12} md={6} key={key}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1.5 }}>
                  <Typography variant="h6">{label}</Typography>
                  <InfoHint title={label}>{hint}</InfoHint>
                </Stack>
                <Slider
                  value={values[key]}
                  onChange={(_, value) => updateLinkAnalysis({ [key]: value as number })}
                  min={min}
                  max={max}
                  step={step}
                  valueLabelDisplay="auto"
                />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Button size="small" sx={{ mt: 2 }} onClick={resetDefaults}>
        Reset all preferences to defaults
      </Button>
    </>
  );
};

// ------------------------------------------------------------- time & locale

const TimeSection: React.FC = () => {
  const { settings, updateTemporal } = useSettingsStore();
  const browserTimeZone = getBrowserTimeZone();
  const timezoneOptions = getTimeZoneOptions();
  const selected = timezoneOptions.includes(settings.temporal.preferredTimezone)
    ? settings.temporal.preferredTimezone
    : LOCAL_TIMEZONE_PREFERENCE;

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Time &amp; locale</Typography>
        <InfoHint title="Time &amp; locale">
          The timezone used for wall-clock query windows, schedules, and time-based dashboard
          displays.
        </InfoHint>
      </Stack>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1.5 }}>
                <Typography variant="h6">Preferred timezone</Typography>
                <InfoHint title="Preferred timezone">
                  Used when creating scheduled analyses, Quick Launch exercise windows, and
                  preferred-timezone query scopes.
                </InfoHint>
              </Stack>
              <FormControl fullWidth>
                <InputLabel>Timezone</InputLabel>
                <Select
                  value={selected}
                  label="Timezone"
                  onChange={(e) => updateTemporal({ preferredTimezone: e.target.value })}
                >
                  <MenuItem value={LOCAL_TIMEZONE_PREFERENCE}>
                    User local timezone ({browserTimeZone})
                  </MenuItem>
                  {timezoneOptions.map((timezone) => (
                    <MenuItem key={timezone} value={timezone}>
                      {timezone}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

// ----------------------------------------------------------------- the page

const SettingsPage: React.FC = () => {
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.roles?.includes('administrator') || false;
  const canManageAccess = isAdmin || Boolean(user?.roles?.includes('user_access_administrator'));
  const sections = useMemo<SettingsSection[]>(() => {
    const out: SettingsSection[] = [
      {
        id: 'appearance',
        label: 'Appearance',
        group: 'Preferences',
        icon: <PaletteIcon fontSize="small" />,
        keywords: 'theme dark light color scheme custom dashboard widget headers',
        hint: 'Theme and color',
        render: () => <AppearanceSection />,
      },
      {
        id: 'graphs',
        label: 'Graphs',
        group: 'Preferences',
        icon: <HubIcon fontSize="small" />,
        keywords: 'link analysis network force layout node size distance charge opacity',
        hint: 'Link Analysis layout',
        render: () => <GraphsSection />,
      },
      {
        id: 'time',
        label: 'Time & locale',
        group: 'Preferences',
        icon: <ScheduleIcon fontSize="small" />,
        keywords: 'timezone clock schedule locale utc',
        hint: 'Clocks and schedules',
        render: () => <TimeSection />,
      },
    ];

    if (isAdmin) {
      out.push({
        id: 'devices',
        label: 'Device Reference',
        group: 'Administration',
        icon: <SettingsIcon fontSize="small" />,
        keywords: 'device reference identity names phones trackers',
        hint: 'Roster and kit IDs',
        render: () => <DeviceReferenceManager />,
      });
      out.push({
        id: 'itinerary',
        label: 'Itinerary',
        group: 'Administration',
        icon: <RouteIcon fontSize="small" />,
        keywords: 'itinerary coordination admin submissions reset course',
        hint: 'Reset a course family',
        render: () => <ItineraryAdminPanel />,
      });
      out.push({
        id: 'modules',
        label: 'Modules & Flags',
        group: 'Administration',
        icon: <AdminPanelIcon fontSize="small" />,
        keywords: 'modules feature flags api docs enable disable',
        hint: 'Tabs and wording',
        render: () => <FeatureFlagsAdminPanel />,
      });
      out.push({
        id: 'monitor-modules',
        label: 'Monitor Modules',
        group: 'Administration',
        icon: <PhoneAndroidIcon fontSize="small" />,
        keywords: 'monitor phone app modules publish bundle release install android',
        hint: 'Phone app modules',
        render: () => <MonitorModulesAdminPanel />,
      });
      out.push({
        id: 'pathfinder',
        label: 'Pathfinder Filters',
        group: 'Administration',
        icon: <MapIcon fontSize="small" />,
        keywords: 'pathfinder track filters speed gap map',
        hint: 'Org map defaults',
        render: () => <PathfinderTrackFiltersAdmin />,
      });
      out.push({
        id: 'cameras',
        label: 'Camera Locations',
        group: 'Administration',
        icon: <CameraLocationsIcon fontSize="small" />,
        keywords: 'camera overlays locations',
        hint: 'Camera overlays',
        render: () => <CameraOverlaysAdminPanel />,
      });
      out.push({
        id: 'reference-layers',
        label: 'Reference Layers',
        group: 'Administration',
        icon: <ReferenceLayersIcon fontSize="small" />,
        keywords: 'reference layers boundaries districts polygons shapefile kml geojson gang beats',
        hint: 'Boundaries, districts, infrastructure',
        render: () => <ReferenceLayersAdminPanel />,
      });
    }

    if (canManageAccess) {
      out.push({
        id: 'access',
        label: 'Access Control',
        group: 'Administration',
        icon: <AdminPanelIcon fontSize="small" />,
        keywords: 'access control users roles permissions',
        hint: 'Groups and scopes',
        render: () => <AccessControlAdminPanel />,
      });
    }

    if (isAdmin && MQTTConnectionSettings) {
      const Component = MQTTConnectionSettings;
      out.push({
        id: 'mqtt',
        label: 'Integrations (MQTT)',
        group: 'Administration',
        icon: <VisibilityIcon fontSize="small" />,
        keywords: 'mqtt broker connection ingest integration',
        hint: 'Broker connection',
        render: () => <Component />,
      });
    }
    if (isAdmin && SystemMetrics) {
      const Component = SystemMetrics;
      out.push({
        id: 'rf-metrics',
        label: 'System Health',
        group: 'Administration',
        icon: <PerformanceIcon fontSize="small" />,
        keywords: 'rf metrics system health ingest throughput',
        hint: 'Ingest and RF',
        render: () => <Component />,
      });
    }

    return out;
  }, [isAdmin, canManageAccess]);

  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const ids = useMemo(() => new Set(sections.map((s) => s.id)), [sections]);
  const [activeId, setActiveId] = useState(() => {
    if (typeof window === 'undefined') return 'appearance';
    const fromHash = window.location.hash.replace(/^#/, '');
    const fromStore = window.sessionStorage.getItem(SECTION_STORAGE) || '';
    return fromHash || fromStore || 'appearance';
  });

  useEffect(() => {
    if (!ids.has(activeId)) {
      setActiveId(sections[0]?.id || 'appearance');
      return;
    }
    try {
      window.sessionStorage.setItem(SECTION_STORAGE, activeId);
    } catch { /* ignore */ }
    if (window.location.hash.replace(/^#/, '') !== activeId) {
      window.history.replaceState(null, '', `#${activeId}`);
    }
  }, [activeId, ids, sections]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sections;
    return sections.filter(
      (s) => s.label.toLowerCase().includes(q) || s.keywords.includes(q) || s.hint.toLowerCase().includes(q),
    );
  }, [sections, query]);

  const active = visible.find((s) => s.id === activeId)
    || sections.find((s) => s.id === activeId && visible.length === 0)
    || visible[0];

  const groups: Array<'Preferences' | 'Administration'> = ['Preferences', 'Administration'];

  return (
    <Box
      sx={{
        px: { xs: 2, md: 3 },
        pt: { xs: 1, md: 1.25 },
        pb: { xs: 2, md: 2.5 },
        minHeight: 'calc(100vh - 80px)',
        background: (t) =>
          t.palette.mode === 'dark'
            ? 'radial-gradient(1200px 480px at 8% -10%, rgba(180,40,40,0.12), transparent 50%)'
            : 'radial-gradient(1200px 480px at 8% -10%, rgba(180,40,40,0.06), transparent 50%)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          mb: 1.5,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 800, letterSpacing: -0.6, lineHeight: 1.1 }}>
            Settings
          </Typography>
          <InfoHint title="Settings">
            Personal preferences apply to your account. Administration sections are
            role-gated and configure the platform for everyone.
          </InfoHint>
        </Stack>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            px: 1,
            py: 0.35,
            borderRadius: 1,
            border: 1,
            borderColor: 'divider',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          0.10.14
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          minHeight: { xs: 520, md: 'calc(100vh - 200px)' },
          bgcolor: 'background.paper',
          overflow: 'hidden',
          border: 1,
          borderColor: 'divider',
          borderRadius: 3,
        }}
      >
        <Box
          sx={{
            width: { xs: 212, sm: 252 },
            flexShrink: 0,
            borderRight: 1,
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.015)' : 'rgba(0,0,0,0.015)'),
          }}
        >
          <Box sx={{ p: 1.5, pb: 1 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search  ·  /"
              value={query}
              inputRef={searchRef}
              onChange={(e) => setQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' },
              }}
            />
          </Box>
          <List dense sx={{ flex: 1, overflowY: 'auto', pt: 0.25, pb: 1.5 }}>
            {groups.map((group) => {
              const items = visible.filter((s) => s.group === group);
              if (items.length === 0) return null;
              return (
                <React.Fragment key={group}>
                  <ListSubheader
                    disableSticky
                    sx={{
                      lineHeight: 2.4,
                      fontSize: '0.65rem',
                      letterSpacing: 1.1,
                      textTransform: 'uppercase',
                      bgcolor: 'transparent',
                      color: 'text.secondary',
                    }}
                  >
                    {group}
                  </ListSubheader>
                  {items.map((section) => {
                    const selected = active?.id === section.id;
                    return (
                      <ListItemButton
                        key={section.id}
                        selected={selected}
                        onClick={() => setActiveId(section.id)}
                        sx={{
                          borderRadius: 1.5,
                          mx: 1,
                          mb: 0.35,
                          py: 0.85,
                          alignItems: 'flex-start',
                          borderLeft: 3,
                          borderLeftColor: selected ? 'secondary.main' : 'transparent',
                          '&.Mui-selected': {
                            bgcolor: (t) =>
                              t.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'action.selected',
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32, mt: 0.15, color: selected ? 'secondary.main' : 'text.secondary' }}>
                          {section.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={section.label}
                          secondary={section.hint}
                          primaryTypographyProps={{
                            fontSize: '0.86rem',
                            fontWeight: selected ? 700 : 500,
                            lineHeight: 1.25,
                          }}
                          secondaryTypographyProps={{
                            fontSize: '0.68rem',
                            noWrap: true,
                          }}
                        />
                      </ListItemButton>
                    );
                  })}
                </React.Fragment>
              );
            })}
            {visible.length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 1, display: 'block' }}>
                Nothing matches “{query}”.
              </Typography>
            )}
          </List>
        </Box>

        <Box
          key={active?.id}
          sx={{
            flex: 1,
            minWidth: 0,
            p: { xs: 2.25, md: 3.25 },
            overflowX: 'hidden',
            animation: 'settingsIn .22s ease',
            '@keyframes settingsIn': {
              from: { opacity: 0, transform: 'translateY(6px)' },
              to: { opacity: 1, transform: 'none' },
            },
          }}
        >
          {active ? active.render() : (
            <Alert severity="info">
              <AlertTitle>No section selected</AlertTitle>
              Clear the search to see all settings sections.
            </Alert>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default SettingsPage;
